import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search,
  CheckCircle2,
  Clock,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';

export default function EmployerEmployees() {
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const user = await api.auth.me();

      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId);
        } else {
          setEmployees([]);
          setEnrollments([]);
        }
        return;
      }
      
      if (user.employer_id) {
        await loadData(user.employer_id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId) => {
    const [employeesData, enrollmentsData] = await Promise.all([
      api.entities.Employee.filter({ employer_id: employerId, is_active: true }),
      api.entities.Enrollment.filter({ employer_id: employerId })
    ]);
    setEmployees(employeesData);
    setEnrollments(enrollmentsData);
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeEnrollmentCount = (employeeId) => {
    return enrollments.filter(e => e.employee_id === employeeId && e.status === 'active').length;
  };

  const filteredEmployees = employees.filter(emp =>
    `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sendReminder = async (employee) => {
    try {
      await api.integrations.Core.SendEmail({
        to: employee.user_email,
        subject: 'Benefits Enrollment Reminder',
        body: `Hi ${employee.first_name},\n\nThis is a friendly reminder to complete your benefits enrollment. Please log in to your benefits portal to review and select your coverage options.\n\nBest regards,\nHR Team`
      });
      toast.success('Reminder sent successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to send reminder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Employees</h1>
        <p className="text-slate-500 mt-1">
          View employee profiles and enrollment status
        </p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          {isSystemAdmin && (
            <div className="grid gap-2 mb-4">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search employees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Profile Status</TableHead>
                <TableHead>Enrollments</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-medium">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {employee.first_name} {employee.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{employee.user_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{employee.department || '-'}</TableCell>
                    <TableCell>
                      {employee.profile_confirmed ? (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Confirmed
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getEmployeeEnrollmentCount(employee.id)} active
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => sendReminder(employee)}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Send Reminder
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}